import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MapPin, Calendar, Clock, Users, TrendingUp } from 'lucide-react'
import { formatCurrency, formatDateForUI, formatTimeForUI } from '@meiuca/web-foundation'
import { modalitiesManager } from '@/managers/modalitiesManager'
import { DayUseOccurrenceDetailModal } from '@/components/DayUseOccurrenceDetailModal'
import type { HomeUpcomingDayUseOccurrencesComponent } from '../../types/sdui'
import { useComponentImpression, analyticsService } from '@meiuca/analytics'

interface HomeUpcomingDayUseOccurrencesProps {
  config: HomeUpcomingDayUseOccurrencesComponent
}

export function HomeUpcomingDayUseOccurrences({ config }: HomeUpcomingDayUseOccurrencesProps) {
  const { title, occurrences } = config
  const [selectedOccurrenceId, setSelectedOccurrenceId] = useState<string | null>(null)
  const navigate = useNavigate()

  // Track component impression
  const impressionRef = useComponentImpression({
    componentType: 'day_use_occurrences',
    componentKey: config.id
  })

  const sortedOccurrences = useMemo(
    () => [...occurrences].sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()),
    [occurrences]
  )

  const handleOccurrenceClick = (occurrence: typeof occurrences[0]) => {
    // Track occurrence click
    analyticsService.track('day_use_occurrences_card_clicked', {
      component_id: config.id,
      component_type: 'day_use_occurrences',
      occurrence_id: occurrence.id,
      venue_name: occurrence.venueName,
      modality: occurrence.modality,
      price: occurrence.pricePerPersonCents,
      available_spots: occurrence.availableSpots
    })

    setSelectedOccurrenceId(occurrence.id)
  }

  const handleSeeMoreClick = () => {
    // Track see more click
    analyticsService.track('day_use_occurrences_see_more_clicked', {
      component_id: config.id,
      component_type: 'day_use_occurrences',
      target_url: '/rotativos/search'
    })

    navigate('/rotativos/search')
  }

  if (occurrences.length === 0) {
    return null
  }

  return (
    <>
      <section ref={impressionRef} className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-bold text-text-primary">{title}</h2>
          <button
            type="button"
            className="text-sm font-semibold text-brand-primary hover:text-brand-primary/80 transition-colors"
            onClick={handleSeeMoreClick}
          >
            Ver mais
          </button>
        </div>

        <div className="flex gap-4 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
        {sortedOccurrences.map(occurrence => {
          const isUnlimited = occurrence.maxPeople === null || occurrence.maxPeople === undefined
          const occupancyPercentage = !isUnlimited && occurrence.maxPeople
            ? Math.round((occurrence.totalParticipants / occurrence.maxPeople) * 100)
            : null
          const isAlmostFull = occupancyPercentage !== null && occupancyPercentage >= 80
          const isFull = !isUnlimited && occurrence.availableSpots === 0

          return (
            <div
              key={occurrence.id}
              className="group relative w-[280px] shrink-0 overflow-hidden rounded-lg border border-border-light bg-white hover:border-brand-primary hover:shadow-md transition-all duration-200 cursor-pointer"
              onClick={() => handleOccurrenceClick(occurrence)}
            >
              <div className="p-4 space-y-3">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-text-primary">{occurrence.venueName}</p>
                  {occurrence.venueLocation && (
                    <div className="flex items-center gap-1 text-xs text-text-secondary">
                      <MapPin className="h-3.5 w-3.5" />
                      <span>{occurrence.venueLocation}</span>
                    </div>
                  )}
                </div>

                <div className="inline-flex items-center gap-2 rounded-full bg-brand-primary/10 px-3 py-1 text-xs font-medium text-brand-primary">
                  {modalitiesManager.getModalityDescription(occurrence.modality)}
                </div>

                <div className="flex items-center gap-2 text-sm text-text-primary">
                  <Calendar className="h-4 w-4 text-brand-primary" />
                  <span>{formatDateForUI(occurrence.startsAt, 'medium')}</span>
                </div>

                <div className="flex items-center gap-2 text-sm text-text-primary">
                  <Clock className="h-4 w-4 text-text-secondary" />
                  <span>
                    {formatTimeForUI(occurrence.startsAt)} - {formatTimeForUI(occurrence.endsAt)}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-3 border-t border-border-light pt-3">
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Users className="h-4 w-4" />
                    <span>
                      {occurrence.totalParticipants} pessoa{occurrence.totalParticipants === 1 ? '' : 's'}
                      {isUnlimited ? ' (ilimitado)' : ` / ${occurrence.maxPeople ?? occurrence.minPeople}`}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-brand-primary">
                      {formatCurrency(occurrence.pricePerPersonCents)}
                    </div>
                    <div className="text-xs text-text-secondary">por pessoa</div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp className={`h-4 w-4 ${
                      isUnlimited
                        ? 'text-blue-500'
                        : isFull
                        ? 'text-red-500'
                        : isAlmostFull
                        ? 'text-orange-500'
                        : 'text-green-500'
                    }`} />
                    <span className={`text-sm font-medium ${
                      isUnlimited
                        ? 'text-blue-600'
                        : isFull
                        ? 'text-red-600'
                        : isAlmostFull
                        ? 'text-orange-600'
                        : 'text-green-600'
                    }`}>
                      {isUnlimited
                        ? 'Vagas ilimitadas'
                        : isFull
                        ? 'Lotado'
                        : `${occurrence.availableSpots} ${occurrence.availableSpots === 1 ? 'vaga' : 'vagas'}`}
                    </span>
                  </div>
                  {occupancyPercentage !== null ? (
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      isFull
                        ? 'bg-red-100 text-red-700'
                        : isAlmostFull
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {occupancyPercentage}% ocupado
                    </div>
                  ) : (
                    <div className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      Sem limite
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </section>

    <DayUseOccurrenceDetailModal
      occurrenceId={selectedOccurrenceId}
      isOpen={!!selectedOccurrenceId}
      onClose={() => setSelectedOccurrenceId(null)}
    />
  </>
  )
}
